import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Calendar, Clock, Mail, MapPin, Phone, User } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';

import {
  AppointmentModalHeader,
  formatDate,
  validateAppointmentContacts,
  hasContactsErrors,
  type ContactsErrors,
} from './shared';
import { EAppointmentType, TAppointmentModalState } from './types';
import { Lucide } from '../../../index';
import { useIsMobile } from '../../../hooks';

import { useAppointmentModalState } from './store';
import { useAppointmentModalActions } from './store';

type TState = {
  name: string;
  email: string;
  phone: string;
};

interface AppointmentStep2Props {
  onBookAppointment: (data: TAppointmentModalState) => void;
  onClose: (stepNr: number) => void;
  t: (key: string) => string;
  isCreating?: boolean;
  isEditMode?: boolean;
  logoSrc?: string;
  locale?: string;
  isLanding?: boolean;
  direction?: 'ltr' | 'rtl';
}

// Helper function to format time from ISO string
const formatTimeFromISO = (isoString: string, locale?: string): string => {
  const date = new Date(isoString);
  return date.toLocaleTimeString(locale || 'en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: false, // Use 24-hour format for consistency
  });
};

export const AppointmentStep2: React.FC<AppointmentStep2Props> = ({
  onBookAppointment,
  onClose,
  t,
  isCreating = false,
  isEditMode = false,
  logoSrc,
  locale,
  isLanding = false,
  direction = 'ltr',
}) => {
  const isMobile = useIsMobile();
  const [errors, setErrors] = useState<ContactsErrors>({});

  const { setName, setEmail, setPhone, setStep } = useAppointmentModalActions();
  const { projectId, dateTimeSlot, appointmentType, duration, notes, name, email, phone } =
    useAppointmentModalState();

  // Validation using shared validation utilities
  const validateForm = (): boolean => {
    const newErrors = validateAppointmentContacts(
      {
        name,
        email,
        phone,
      },
      t,
    );
    setErrors(newErrors);
    return !hasContactsErrors(newErrors);
  };

  const onInputChange = (field: keyof TState) => {
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (validateForm()) {
      onBookAppointment({
        projectId,
        dateTimeSlot,
        appointmentType,
        duration,
        notes,
        name,
        email,
        phone,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <AppointmentModalHeader
        onBack={() => setStep(1)}
        onClose={() => onClose(2)}
        t={t}
        logoSrc={logoSrc}
        isLanding={isLanding}
        direction={direction}
        step={2}
      />

      <Paper
        elevation={0}
        sx={{
          my: 3,
          p: 2,
          bgcolor: 'grey.100',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.300',
        }}
      >
        <Stack spacing={1}>
          {dateTimeSlot?.start && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lucide icon={Calendar} size={16} />
                <Typography variant="body2" color="text.secondary">
                  {formatDate(new Date(dateTimeSlot?.start), 'long', locale)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lucide icon={Clock} size={16} />
                <Typography variant="body2" color="text.secondary">
                  {formatTimeFromISO(dateTimeSlot?.start, locale)}
                </Typography>
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {appointmentType === EAppointmentType.GOOGLE_MEET ? (
              <img
                src="https://static.treedis.com/scheduling/email-templates/assets/google_meet_icon.png"
                alt="Google Meet"
                style={{ width: 20, height: 20, borderRadius: 0.5 }}
              />
            ) : (
              <Lucide icon={MapPin} size={16} />
            )}
            <Typography variant="body2" color="text.secondary">
              {appointmentType === EAppointmentType.GOOGLE_MEET
                ? t('scheduling.appointment.googleMeet')
                : t('scheduling.appointment.physicalMeeting')}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Stack spacing={0.5}>
          {!isMobile && (
            <Typography variant="body2" color="grey.700" fontWeight={500}>
              {t('scheduling.appointment.name')}
            </Typography>
          )}
          <TextField
            name="name"
            size="small"
            fullWidth
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              onInputChange('name');
            }}
            placeholder={t('scheduling.appointment.namePlaceholder')}
            required
            disabled={isEditMode}
            error={!!errors.name}
            helperText={errors.name}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Lucide icon={User} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>

        <Stack spacing={0.5}>
          {!isMobile && (
            <Typography variant="body2" color="grey.700" fontWeight={500}>
              {t('scheduling.appointment.phone')}
            </Typography>
          )}
          <TextField
            name="phone"
            type="tel"
            size="small"
            fullWidth
            value={phone}
            onChange={(event) => {
              setPhone(event.target.value);
              onInputChange('phone');
            }}
            placeholder={t('scheduling.appointment.phonePlaceholder')}
            required
            disabled={isEditMode}
            error={!!errors.phone}
            helperText={errors.phone}
            sx={{
              '& input[type="tel"]': {
                textAlign: 'inherit', // Inherit text alignment from parent (RTL context)
                direction: 'inherit', // Inherit text direction from parent
              },
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Lucide icon={Phone} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>

        <Stack spacing={0.5}>
          {!isMobile && (
            <Typography variant="body2" color="grey.700" fontWeight={500}>
              {t('scheduling.appointment.email')}
            </Typography>
          )}
          <TextField
            name="email"
            type="email"
            size="small"
            fullWidth
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              onInputChange('email');
            }}
            placeholder={t('scheduling.appointment.emailPlaceholder')}
            required
            disabled={isEditMode}
            error={!!errors.email}
            helperText={errors.email}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Lucide icon={Mail} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      </Stack>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        type="submit"
        disabled={isCreating}
        startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {isCreating
          ? isEditMode
            ? t('scheduling.appointment.updating')
            : t('scheduling.appointment.creating')
          : isEditMode
          ? t('scheduling.appointment.updateAppointment')
          : t(`scheduling.appointment.${isLanding ? 'bookAppointmentLanding' : 'bookAppointment'}`)}
      </Button>
    </form>
  );
};
